import { EHeadingStyleVariant, ETextStyleVariant, Heading, Text } from '@outdoorsyco/bonfire';
import React, { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { EscapeTile } from '@/components/common/EscapeTile';
import { AnimateInsertion } from '@/components/ui/AnimateInsertion';
import { getBundles } from '@/redux/modules/bundles';
import { getBundlesSelector, getIsBundlesLoadingSelector } from '@/redux/selectors/bundles';
import { trackEscapeBundleSelectedEvent } from '@/services/analytics/listings';
import { EListingSource } from '@/services/analytics/listings/types';
import { ERentalType } from '@/services/analytics/types';
import { isProdDeploy } from '@/utility/isSSR';
import { IBundleTile } from '@/utility/mapSearchResultToTile';

const CONSTRAIN_CAMPGROUND_IDS = [
  92, 73, 21, 93, 15, 75, 25, 71, 24, 205, 12, 116, 89, 72, 119, 206, 184, 15,
];

export const ExploreTopDestinations = () => {
  const dispatch = useDispatch();
  const bundles = useSelector(getBundlesSelector);
  const isLoading = useSelector(getIsBundlesLoadingSelector);

  // Bundles are returned from API sorted by distance from the user.
  // Product wanted some specific constranin and order
  // is a fast change until we have a better solution.
  const sortedBundles: IBundleTile[] = useMemo(() => {
    if (!bundles || bundles.length === 0) {
      return [];
    }
    if (!isProdDeploy()) {
      return bundles;
    }

    // filter to only include the constrained campground IDs
    const constrained = bundles.filter(bundle => CONSTRAIN_CAMPGROUND_IDS.includes(bundle.id));

    // order
    const sorted = [...CONSTRAIN_CAMPGROUND_IDS]
      .map(id => constrained.find(bundle => bundle.id === id))
      .filter((bundle): bundle is IBundleTile => !!bundle);

    return sorted;
  }, [bundles]);

  useEffect(() => {
    dispatch(getBundles());
  }, [dispatch]);

  const onClick = (bundle: IBundleTile) => {
    trackEscapeBundleSelectedEvent({
      rentalID: bundle.id,
      rentalType: ERentalType.ESCAPE,
      isCampground: false,
      source: EListingSource.VACATION_PACKAGES_LANDING_PAGE,
      isEscape: true,
      campgroundAddress: bundle.location,
      campgroundName: bundle.name,
    });
  };

  if (!sortedBundles || sortedBundles.length === 0 || isLoading) {
    return null;
  }

  return (
    <AnimateInsertion>
      <div>
        <div className="flex flex-col mb-5 md:mb-10 md:gap-5 gap-7">
          <Heading
            variant={EHeadingStyleVariant.H2Alt}
            className="not-italic font-normal text-850 md:text-900 leading-[35px]">
            <FormattedMessage
              defaultMessage="Stay At These Featured RV Resorts, Parks and Campgrounds"
              id="oxIvrC"
            />
          </Heading>
          <Text variant={ETextStyleVariant.SmallRegular}>
            <FormattedMessage
              defaultMessage="Enjoy top-tier amenities—pools, hot tubs, fitness centers, dog parks, restaurants, and more"
              id="UjV80f"
            />
          </Text>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 md:gap-x-5 gap-y-10 md:gap-y-18">
          {sortedBundles?.map((bundle, index) => (
            <EscapeTile
              key={index}
              bundle={bundle}
              wishlistingEventData={{
                index,
                eventSource: EListingSource.VACATION_PACKAGES_LANDING_PAGE,
              }}
              onClick={() => onClick(bundle)}
            />
          ))}
        </div>
      </div>
    </AnimateInsertion>
  );
};
